/**
 * Compresses an image file to below 1MB using Canvas API
 * @param file - The image file to compress
 * @param maxSizeMB - Maximum size in MB (default: 1MB)
 * @param quality - Initial quality (default: 0.9)
 * @returns Promise<File> - Compressed image file
 */
export async function compressImage(
  file: File,
  maxSizeMB = 1,
  quality = 0.9
): Promise<File> {
  return new Promise((resolve, reject) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions if image is too large (max 1920px width)
        const maxWidth = 1920;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Determine output format (always use JPEG for better compression, except for PNG transparency)
        const outputType = file.type === 'image/png' && file.size > maxSizeBytes ? 'image/jpeg' : (file.type === 'image/gif' ? 'image/jpeg' : file.type || 'image/jpeg');
        
        // Try different quality levels and dimensions to get below max size
        const tryCompress = (currentQuality: number, currentWidth: number, currentHeight: number, attempt = 0): void => {
          // Limit attempts to prevent infinite loops
          if (attempt > 50) {
            reject(new Error('Failed to compress image to required size'));
            return;
          }
          
          // Resize canvas if needed
          if (canvas.width !== currentWidth || canvas.height !== currentHeight) {
            canvas.width = currentWidth;
            canvas.height = currentHeight;
            ctx.drawImage(img, 0, 0, currentWidth, currentHeight);
          }
          
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              
              // If blob is already below max size, return it
              if (blob.size <= maxSizeBytes) {
                const compressedFile = new File(
                  [blob],
                  file.name.replace(/\.[^/.]+$/, '') + (outputType === 'image/jpeg' ? '.jpg' : '.png'),
                  {
                    type: outputType,
                    lastModified: Date.now(),
                  }
                );
                resolve(compressedFile);
                return;
              }
              
              // If quality is already very low, try reducing dimensions
              if (currentQuality <= 0.2) {
                // Reduce dimensions by 10% and reset quality
                const newWidth = Math.max(Math.floor(currentWidth * 0.9), 400);
                const newHeight = Math.max(Math.floor(currentHeight * 0.9), 400);
                tryCompress(0.8, newWidth, newHeight, attempt + 1);
                return;
              }
              
              // Reduce quality and try again
              const newQuality = Math.max(currentQuality - 0.05, 0.1);
              tryCompress(newQuality, currentWidth, currentHeight, attempt + 1);
            },
            outputType,
            Math.max(currentQuality, 0.1)
          );
        };
        
        tryCompress(quality, width, height);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
}

/**
 * Checks if image needs compression and compresses it if necessary
 * @param file - The image file to check and compress
 * @param showToast - Optional callback to show toast notification
 * @returns Promise<File> - Original or compressed file
 */
export async function compressImageIfNeeded(
  file: File,
  showToast?: (message: string, type: 'success' | 'error' | 'info') => void
): Promise<File> {
  const maxSizeBytes = 1 * 1024 * 1024; // 1MB
  const thresholdBytes = 900 * 1024; // 900KB
  
  // If file is already below 1MB, return as is
  if (file.size <= maxSizeBytes) {
    return file;
  }
  
  // Show toast if file is above threshold
  if (file.size > thresholdBytes && showToast) {
    const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    showToast(`Image size is ${originalSizeMB}MB. Compressing...`, 'info');
  }
  
  try {
    // Always compress if file is above 1MB, use more aggressive compression for larger files
    const initialQuality = file.size > 5 * 1024 * 1024 ? 0.7 : 0.85; // Lower quality for very large files
    const compressedFile = await compressImage(file, 1, initialQuality);
    
    // Verify compression worked
    if (compressedFile.size > maxSizeBytes) {
      // If still too large, try again with even more aggressive compression
      const moreCompressedFile = await compressImage(file, 1, 0.5);
      
      // Show success toast if compression happened and file was above threshold
      if (file.size > thresholdBytes && showToast) {
        const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        const compressedSizeMB = (moreCompressedFile.size / (1024 * 1024)).toFixed(2);
        showToast(
          `Image compressed successfully from ${originalSizeMB}MB to ${compressedSizeMB}MB`,
          'success'
        );
      }
      
      return moreCompressedFile;
    }
    
    // Show success toast if compression happened and file was above threshold
    if (file.size > thresholdBytes && showToast) {
      const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const compressedSizeMB = (compressedFile.size / (1024 * 1024)).toFixed(2);
      showToast(
        `Image compressed successfully from ${originalSizeMB}MB to ${compressedSizeMB}MB`,
        'success'
      );
    }
    
    return compressedFile;
  } catch (error) {
    console.error('Image compression error:', error);
    if (showToast) {
      showToast('Failed to compress image. Please try again.', 'error');
    }
    throw error;
  }
}

